<template>
  <!-- Main Content with offset for fixed header -->
  <main>
    <!-- Hero Slider -->
    <section class="relative overflow-hidden"
      @mouseenter="stopAutoSlide"
      @mouseleave="startAutoSlide">
      <!-- Slider Container -->
      <div class="relative h-[100vh]">
        <!-- Slide -->
        <template v-for="(slide, index) in data.sliders.list" :key="slide.id">
          <div v-if="currentSlide === index" class="slide-background absolute inset-0 flex items-end px-4"  :style="(slide.image)?{ backgroundImage: `url(${require(`@/${slide.image}`)})`, backgroundSize: 'cover', backgroundPosition: 'center' }:''">
            <video v-if="slide.video" class="absolute inset-0 w-full h-full object-cover" autoplay muted loop playsinline>
              <source :src="require(`@/${slide.video}`)" type="video/mp4">
            </video>
            <div class="max-w-6xl mx-auto w-full relative z-10 bottom-10">
              <div class="max-w-2xl">
                <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white titlecase" :class="{ 'animate-slideInLeft': heroVisible }">
                  {{ slide.title }}
                </h1>
              </div>
            </div>
          </div>
        </template>
      </div>
      
      <!-- Slider Controls -->
      <div v-if="data.sliders.list && data.sliders.list.length > 1" class="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
        <button 
          v-for="(slide, index) in data.sliders.list" 
          :key="slide.id"
          @click="goToSlide(index)"
          :class="['w-3 h-3 rounded-full transition-all duration-300', currentSlide === index ? 'bg-red-700 w-8' : 'bg-gray-400 hover:bg-gray-600']"
          :aria-label="`Go to slide ${index + 1}`"
        ></button>
      </div>

      <div class="flex justify-between w-full px-4 absolute bottom-0 md:opacity-0">
        <!-- arrow -->
        <button 
          v-if="data.sliders.list && data.sliders.list.length > 1"
          @click="prevSlide"
          class="left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition duration-300 z-20"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
        </button>
        <button 
          v-if="data.sliders.list && data.sliders.list.length > 1"
          @click="nextSlide"
          class="right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition duration-300 z-20"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
          </svg>
        </button>
      </div>
      
      <!-- Navigation Arrows -->
      <div class="opacity-0 md:opacity-100">
        <button 
          v-if="data.sliders.list && data.sliders.list.length > 1"
          @click="prevSlide"
          class="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition duration-300 z-20"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
        </button>
        <button 
          v-if="data.sliders.list && data.sliders.list.length > 1"
          @click="nextSlide"
          class="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition duration-300 z-20"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
          </svg>
        </button>
      </div>
    </section>

    <!-- Services Grid -->
    <section class="py-10 px-4 bg-gray-50" ref="servicesSection">
      <div class="max-w-6xl mx-auto">
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <!-- "How can we help" Card -->
          <div v-if="data.services.how" class="col-span-3 mb-7" :class="{ 'animate-slideInUp': howCareVisible }" ref="howCareSection">
            <!-- Content -->
            <div class="relative z-10">
              <div class="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                <!-- Left Section -->
                <div :class="{ 'animate-slideInLeft': howCareVisible }">
                  <h3 class="text-4xl md:text-5xl font-bold mb-6 text-red-600 leading-tight">
                    {{ data.services.how.title }}
                  </h3>
                  <p class="text-lg leading-relaxed text-justify mb-2" :class="{ 'animate-slideInUp': howCareVisible }" :style="howCareVisible ? { 'animation-delay': '0.2s' } : {}" v-html="data.services.how.desc"></p>
                  <router-link to="/services" class="inline-block border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white font-semibold py-3 px-8 rounded-md transition duration-300 uppercase">
                    Learn more
                  </router-link>
                </div>
                <!-- Benefit Item 1 -->
                <div class="" :class="{ 'animate-slideInRight': howCareVisible }">
                  <div class="grid grid-cols-2 gap-4">
                    <!-- Dynamic Image Boxes -->
                    <template v-if="data.about.stats && data.about.stats.length">
                      <div 
                        v-for="(box, index) in data.about.list" 
                        :key="index"
                        :class="[
                          'h-64 rounded-lg border flex flex-col justify-center transition-all duration-300 hover:shadow-md p-6',
                          index === 0 ? 'bg-gradient-to-br from-gray-900 to-gray-900 border-gray-900' :
                          index === 1 ? 'bg-gradient-to-br from-gray-900 to-gray-900 border-gray-900 mt-[100px]' :
                          'bg-gradient-to-br from-gray-900 to-gray-900 border-gray-900 mt-[-100px]',
                          { 'animate-scaleInUp': howCareVisible }
                        ]"
                        :style="howCareVisible ? { 'animation-delay': (index * 0.2) + 's' } : {}"
                      >
                        <!-- Text -->
                        <span 
                          :class="[
                            'font-semibold text-2xl',
                            index === 1 ? 'text-white' : 'text-white'
                          ]"
                        >
                          {{ box.title }}
                        </span>
                        <p class="text-lg text-white">{{ box.desc }}</p>
                      </div>
                    </template>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="" ref="serviceCardsSection">
        <div class="grid md:grid-cols-3 lg:grid-cols-6">

          <!-- Dynamic Service Cards -->
          <template v-if="data.solutions.list && data.solutions.list.length">
            <router-link
              v-for="service in data.solutions.list" 
              :key="service.id"
              :to="'/solutions/'+service.title.replace(/\s+/g, '-').toLowerCase()"
              class="bg-white rounded-lg shadow-md hover:shadow-lg transition duration-300" :class="{ 'animate-slideInUp': serviceCardsVisible }" :style="serviceCardsVisible ? { 'animation-delay': (service.id * 0.15) + 's' } : {}"
            >
              <!-- Image banner with title overlay -->
              <div class="overflow-hidden h-[500px] bg-gray-100">
                <div class="h-full bg-cover bg-center relative" :style="(service.image)?{ backgroundImage: `url(${require(`@/${service.image}`)})` }:''">
                   <video v-if="service.video" class="absolute inset-0 w-full h-full object-cover" autoplay muted loop playsinline>
                    <source :src="require(`@/${service.video}`)" type="video/mp4">
                  </video>
                  <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                    <div>
                      <h4 class="text-white text-xl font-semibold drop-shadow">{{ service.title }}</h4>
                      <p class="text-white text-sm" v-for="(tt,index) in service.title_list" :key="'iii'+index">{{ tt }}</p>
                    </div>
                    <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                    </svg>
                  </div>
                </div>
              </div>
            </router-link>
          </template><!-- Dynamic Service Cards -->
          <template v-if="data.services.list && data.services.list.length">
            <router-link
              v-for="service in data.services.list.slice(1,3)" 
              :key="service.id"
              :to="'/services/'+service.title.replace(/\s+/g, '-').toLowerCase()"
              class="bg-white rounded-lg shadow-md hover:shadow-lg transition duration-300" :class="{ 'animate-slideInUp': serviceCardsVisible }" :style="serviceCardsVisible ? { 'animation-delay': (service.id * 0.15) + 's' } : {}"
            >
              <!-- Image banner with title overlay -->
              <div class="overflow-hidden h-[500px] bg-gray-100">
                <div class="h-full bg-cover bg-center relative" :style="(service.image)?{ backgroundImage: `url(${require(`@/${service.image}`)})` }:''">
                  <video v-if="service.video" class="absolute inset-0 w-full h-full object-cover" autoplay muted loop playsinline>
                    <source :src="require(`@/${service.video}`)" type="video/mp4">
                  </video>
                  <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                    <div>
                      <h4 class="text-white text-xl font-semibold drop-shadow">{{ service.title }}</h4>
                      <p class="text-white text-sm" v-for="(tt,index) in service.title_list" :key="'iii'+index">{{ tt }}</p>
                    </div>
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                    </svg>
                  </div>
                </div>
              </div>
            </router-link>
          </template>
      </div>
    </section>

    <!-- About Section -->
    <section class="py-10 px-4 bg-gray-50" ref="aboutSection">
      <div class="max-w-6xl mx-auto">
        <div class="flex flex-col lg:flex-row gap-12 items-center mb-5">
          <div class="" :class="{ 'animate-slideInLeft': aboutVisible }">
            <h2 class="text-3xl md:text-4xl font-bold mb-6" :class="{ 'animate-slideInUp': aboutVisible }">
              {{ data.about.group.title }}
            </h2>
            <p class="text-lg text-justify" :class="{ 'animate-slideInUp': aboutVisible }" :style="aboutVisible ? { 'animation-delay': '0.2s' } : {}" v-html="data.about.group.desc"></p>
            <div class="w-full text-right">
              <router-link to="/about" class="inline-block border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white bg-white font-semibold py-3 px-8 rounded-md transition duration-300 uppercase" :class="{ 'animate-slideInUp': aboutVisible }" :style="aboutVisible ? { 'animation-delay': '0.4s' } : {}">
                learn more
              </router-link>
            </div>
          </div>
        </div>
        
        <!-- Statistics Row -->
        <section class="mb-10 bg-gray-900 text-white py-12 rounded-lg" ref="statsSection">
          <div class="max-w-7xl mx-auto px-4">
            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-center" :class="{ 'animate-slideInUp': aboutVisible }">
              <div v-for="(stat, index) in data.about.stats" :key="index">
                <p class="text-3xl md:text-4xl font-bold" v-if="index <= 3">
                  {{ this.animatedNumbers[index] }}+
                </p>
                <p class="text-3xl md:text-4xl font-bold" v-else-if="index == 4">
                  {{ this.animatedNumbers[index] }}/4
                </p>
                <p class="text-3xl md:text-4xl font-bold" v-else>
                  {{ this.animatedNumbers[index] }}
                </p>
                <p class="text-sm mt-1">{{ stat.label }}</p>
              </div>
            </div>
          </div>
        </section>

        <div>
          <h2 class="text-xl font-bold mb-2" :class="{ 'animate-slideInUp': aboutVisible }">
            {{ data.about.integrate.title }}
          </h2>
          <p class="text-lg text-justify" :class="{ 'animate-slideInUp': aboutVisible }" :style="aboutVisible ? { 'animation-delay': '0.2s' } : {}" v-html="data.about.integrate.desc"></p>
          <div class="grid grid-cols-4 md:flex gap-2 items-center justify-evenly py-5">
            <div v-for="(integrate, index) in data.about.integrate.list" :key="index" class="inline-block border-2 bg-gray-900 text-white hover:bg-gray-900 hover:text-white font-semibold py-3 px-8 rounded-full h-[100px] w-[100px] transition duration-300 uppercase justify-center flex items-center text-sm">
              {{ integrate }}
            </div>
          </div>
        </div>
        <div class="py-10">
          <h2 class="text-xl font-bold mb-2 uppercase" :class="{ 'animate-slideInUp': aboutVisible }">
            {{ data.about.serve.title }}
          </h2>
          <!-- <img :src="require(`@/assets/serve.jpg`)" class="w-full mx-auto rounded-lg mb-[-50px]" /> -->
          <div class="bg-gray-900 text-white mt-5 rounded-lg overflow-hidden">
            <div class="h-[400px] bg-cover bg-center relative" :style="{ backgroundImage: `url(${require(`@/assets/serve.jpg`)})` }"></div>
            <div class="p-5">
              <div class="grid md:grid-cols-2 gap-10 mt-5">
                <p class="text-lg text-justify" :class="{ 'animate-slideInUp': aboutVisible }" :style="aboutVisible ? { 'animation-delay': '0.2s' } : {}" v-html="data.about.serve.desc"></p>
                <div class="grid md:grid-cols-2 gap-2 items-center justify-evenly text-lg">
                  <div v-for="(serve, index) in data.about.serve.industries" :key="index">
                    {{ serve }}
                  </div>
                </div>
              </div>
              <p class="text-center mt-10 text-lg">{{ data.about.serve.tagline }}</p>
            </div>
          </div>
        </div>
        <div class="py-10">
          <h2 class="text-2xl font-bold mb-2 uppercase text-center" :class="{ 'animate-slideInUp': aboutVisible }">
            {{ data.about.contact.title }}
          </h2>
          <p class="text-lg text-center" :class="{ 'animate-slideInUp': aboutVisible }" :style="aboutVisible ? { 'animation-delay': '0.2s' } : {}" v-html="data.about.contact.desc"></p>
          <div class="grid grid-cols-2 gap-10 mt-10">
            <div v-for="(section, index) in data.about.contact.sections" :key="index" class="border-t-4 border-blue-900 pt-5">
              <h2 class="text-2xl font-bold mb-2 titlecase" :class="{ 'animate-slideInUp': aboutVisible }">
                {{ section.heading }}
              </h2>
              <p class="text-lg my-5" :class="{ 'animate-slideInUp': aboutVisible }" :style="aboutVisible ? { 'animation-delay': '0.2s' } : {}" v-html="section.desc"></p>
              <router-link to="/contact" class="inline-block border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white bg-white font-semibold py-3 px-8 rounded-md transition duration-300 uppercase" :class="{ 'animate-slideInUp': aboutVisible }" :style="aboutVisible ? { 'animation-delay': '0.4s' } : {}">
                {{ section.action }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import homeData from '@/data/home.json'

export default {
  name: 'HomePage',
  data() {
    return {
      currentSlide: 0,
      slideInterval: null,
      data: homeData,
      autoSlideDelay: 3000,
      animatedNumbers: [0, 0, 0, 0, 0, 0],
      animationSpeed: 50,
      hasAnimated: false,
      observer: null,
      heroVisible: true,
      howCareVisible: false,
      serviceCardsVisible: false,
      aboutVisible: false,
      sectionObserver: null,
    }
  },
  computed: {
    totalSlides() {
      return this.data.sliders.list ? this.data.sliders.list.length : 0
    }
  },
  mounted() {
    this.startAutoSlide()
    this.resetAnimation()
    this.setupIntersectionObserver()
    this.setupSectionObserver()
  },
  beforeUnmount() {
    this.stopAutoSlide()
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.sectionObserver) {
      this.sectionObserver.disconnect()
    }
  },
  methods: {
    nextSlide() {
      if (this.totalSlides === 0) return
      
      this.currentSlide = (this.currentSlide + 1) % this.totalSlides
      this.restartAutoSlide()
    },
    prevSlide() {
      if (this.totalSlides === 0) return
      
      this.currentSlide = (this.currentSlide - 1 + this.totalSlides) % this.totalSlides
      this.restartAutoSlide()
    },
    goToSlide(index) {
      if (index >= 0 && index < this.totalSlides) {
        this.currentSlide = index
        this.restartAutoSlide()
      }
    },
    startAutoSlide() {
      if (this.totalSlides <= 1) return
      
      this.stopAutoSlide()
      this.slideInterval = setInterval(() => {
        this.nextSlide()
      }, this.autoSlideDelay)
    },
    stopAutoSlide() {
      if (this.slideInterval) {
        clearInterval(this.slideInterval)
        this.slideInterval = null
      }
    },
    restartAutoSlide() {
      this.stopAutoSlide()
      this.startAutoSlide()
    },
    resetAnimation() {
      this.animatedNumbers = [0, 0, 0, 0, 0, 0]
      this.hasAnimated = false
    },
    setupIntersectionObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }

      // if (!('IntersectionObserver' in window)) {
      //   setTimeout(() => {
      //     this.startNumberAnimation()
      //   }, 1000)
      //   return
      // }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.hasAnimated) {
              this.startNumberAnimation()
              this.hasAnimated = true

              if (this.observer && entry.target) {
                this.observer.unobserve(entry.target)
              }
            }
          })
        },
        {
          threshold: 0.3,
          rootMargin: '0px 0px -100px 0px'
        }
      )

      this.$nextTick(() => {
        const section = this.$refs.aboutSection
        if (section) {
          this.observer.observe(section)
        }
      })
    },
    
    startNumberAnimation() {
      if (this.data.about.stats) {
        this.data.about.stats.forEach((target, index) => {
          this.animateNumber(index, target.value)
        })
      }
    },
    
    animateNumber(index, target) {
      const increment = target / 40
      let current = 0
      
      const timer = setInterval(() => {
        current += increment
        if (current >= target) {
          current = target
          clearInterval(timer)
        }
        this.animatedNumbers[index] = Math.floor(current)
      }, this.animationSpeed)
    },
    
    setupSectionObserver() {
      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const ref = entry.target.getAttribute('data-section')
              console.log(ref)
              if (ref === 'howCare') {
                this.howCareVisible = true
              } else if (ref === 'serviceCards') {
                this.serviceCardsVisible = true
              } else if (ref === 'about') {
                this.aboutVisible = true
                this.startNumberAnimation()
              }
            }
          })
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px'
        }
      )
      

      if (this.$refs.howCareSection) {
        this.$refs.howCareSection.setAttribute('data-section', 'howCare')
        this.sectionObserver.observe(this.$refs.howCareSection)
      }
      if (this.$refs.serviceCardsSection) {
        this.$refs.serviceCardsSection.setAttribute('data-section', 'serviceCards')
        this.sectionObserver.observe(this.$refs.serviceCardsSection)
      }
      if (this.$refs.aboutSection) {
        this.$refs.aboutSection.setAttribute('data-section', 'about')
        this.sectionObserver.observe(this.$refs.aboutSection)
      }
    }
  }
}
</script>

<style scoped>
.slide-background::before {
  content: '';
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 5;
}

/* Fade In Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.animate-fadeIn {
  animation: fadeIn 0.8s ease-in-out both;
}

/* Fade In Up Animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeInUp {
  animation: fadeInUp 0.8s ease-out both;
}

/* Slide In Left Animation */
@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-slideInLeft {
  animation: slideInLeft 0.8s ease-out both;
}

/* Slide In Right Animation */
@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-slideInRight {
  animation: slideInRight 0.8s ease-out both;
}

/* Slide In Up Animation */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slideInUp {
  animation: slideInUp 0.8s ease-out both;
}

/* Scale In Animation */
@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-scaleIn {
  animation: scaleIn 0.6s ease-out both;
}

/* Scale In Up Animation */
@keyframes scaleInUp {
  from {
    opacity: 0;
    transform: scaleY(0.8) translateY(30px);
  }
  to {
    opacity: 1;
    transform: scaleY(1) translateY(0);
  }
}

.animate-scaleInUp {
  animation: scaleInUp 0.7s ease-out both;
}
</style>